import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { formatISO } from "date-fns";
import {
  AnswerListType,
  AnswerRangeType,
  QuizResponseType,
  QuizType,
  ImperialMetricDisplayPreference,
  QuestionType,
  SelectionMechanism,
  UserType,
} from "@WahooFitness/cloud-client-types";
import DateOfBirthInput from "../User/AboutYou/DateOfBirthInput";
import { OnboardingWeightInput } from "../AthleteOnboarding/StepComponents/OnboardingWeightInput";
import { OnboardingHeightInput } from "../AthleteOnboarding/StepComponents/OnboardingHeightInput";
import { StepContent } from "../AthleteOnboarding/CustomStepper";
import useAboutYou from "../User/AboutYou/useAboutYou";
import NumericEntry from "@/components/Quiz/NumericEntry/NumericEntry";
import NumericDistanceEntry from "@/components/Quiz/NumericEntry/NumericDistanceEntry";
import SingleSelect from "@/components/Quiz/SingleSelect/SingleSelect";
import MultipleSelect from "@/components/Quiz/MultiSelect/MultipleSelect";
import Ranking from "@/components/Quiz/Ranking/Ranking";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import { t } from "@lingui/macro";
import { useHeaderContext } from "@WahooFitness/wahoo-offline-mfe";
import MultiNumericEntry from "./MultiNumericEntry/MultiNumericEntry";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";

export type Responses = Record<string, Array<string | Date | number | null>>;

export type SanitizedAnswers = Record<
  string,
  Array<string | number | Record<string, number | null>>
>;

interface QuizConfig<TResults> {
  quizData: QuizType | undefined;
  quizLoading: boolean;
  quizError: unknown;
  getQuizResults: (response: QuizResponseType) => Promise<{ results?: TResults; error?: string }>;
  buildResponse: (
    sanitizedAnswers: SanitizedAnswers,
    questionnaireId: string,
    userId: number | undefined
  ) => QuizResponseType;
  pages: { education: number; results: number };
  analyticsEventName: string;
  analyticsSourcePage: SourcePage;
  onBeforeSubmit?: (responses: Responses) => void;
  onFirstStepBack?: () => void;
}

function useQuiz<TResults>(
  user: UserType,
  setResults: Dispatch<SetStateAction<TResults | undefined>>,
  setPage: Dispatch<SetStateAction<number>>,
  setError: Dispatch<SetStateAction<string | undefined>>,
  skipEducation: boolean,
  config: QuizConfig<TResults>
) {
  const {
    quizData,
    quizLoading,
    quizError,
    getQuizResults,
    buildResponse,
    pages,
    analyticsEventName,
    analyticsSourcePage,
    onBeforeSubmit,
    onFirstStepBack,
  } = config;

  const { navigateBack } = useHeaderContext();
  const { updateUserFields, updateDisplayPreferences } = useAboutYou(user);
  const { runningFitnessProfile } = useFitnessProfiles();
  const [activeStep, setActiveStep] = useState(0);
  const [currentStepValid, setCurrentStepValid] = useState(true);
  const [currentStepHasValue, setCurrentStepHasValue] = useState(false);
  const [userUnitPreference, setUserUnitPreference] = useState(
    user.display_preference?.speed_distance ?? ImperialMetricDisplayPreference.metric
  );
  const { logEvent } = useLogEvent();
  const buttonName =
    activeStep === (quizData?.questions.length || 0) - 1 ? t`See results` : t`Continue`;

  /**
   * Pre-populate the user profile questions (age, height, weight, sex) with the values that are in cloud
   */
  const setInitialResponses = useCallback(
    (quizData: QuizType) => {
      return quizData.questions.reduce((acc, cur) => {
        switch (cur.answers.measurement) {
          case "dob":
            if (user.birth) {
              acc[cur.question_id] = [new Date(user.birth + "T00:00:00")];
            }
            break;
          case "weight":
            if (user.weight) {
              acc[cur.question_id] = [user.weight];
            }
            break;
          case "height":
            if (user.height) {
              acc[cur.question_id] = [user.height];
            }
            break;
          case "sex":
            if (user.gender !== undefined && user.gender !== null) {
              acc[cur.question_id] = [user.gender];
            }
            break;
          case "race_time": {
            const raceDurations: Array<number | null> = [
              runningFitnessProfile?.dur_1 ?? null,
              runningFitnessProfile?.dur_2 ?? null,
              runningFitnessProfile?.dur_3 ?? null,
              runningFitnessProfile?.dur_4 ?? null,
            ];
            if (raceDurations.some((duration) => duration !== null)) {
              acc[cur.question_id] = raceDurations;
            }
            break;
          }
          case "no_measurement":
          default:
            if (cur.answers.selection_mechanism === SelectionMechanism.exactly_all_ordered) {
              acc[cur.question_id] = (cur.answers as AnswerListType).possibility_list.map(
                (i) => i.key
              );
            } else if (cur.answers.selection_mechanism === SelectionMechanism.multi_numeric_entry) {
              const raceDurations: Array<number | null> = [
                runningFitnessProfile?.dur_1 ?? null,
                runningFitnessProfile?.dur_2 ?? null,
                runningFitnessProfile?.dur_3 ?? null,
                runningFitnessProfile?.dur_4 ?? null,
              ];
              if (raceDurations.some((duration) => duration !== null)) {
                acc[cur.question_id] = raceDurations;
              }
            }
        }
        return acc;
      }, {} as Responses);
    },
    [
      user.birth,
      user.gender,
      user.height,
      user.weight,
      runningFitnessProfile?.dur_1,
      runningFitnessProfile?.dur_2,
      runningFitnessProfile?.dur_3,
      runningFitnessProfile?.dur_4,
    ]
  );

  const [responses, setResponses] = useState<Responses>();

  useEffect(() => {
    // We added activeStep === 0 to prevent initialResponses from being set multiple times
    // during the quiz. The useEffect was called every time when the me call returned a new user
    // object from cloud. If it happens after the FTP step is completed, it will reset the responses
    // which ends up in loosing answers and being stuck in the loading screen upon submitting the quiz
    if (!quizLoading && !quizError && quizData && activeStep === 0) {
      setResponses(setInitialResponses(quizData));
    }
  }, [quizLoading, quizError, quizData, setInitialResponses, activeStep]);

  /**
   * Turns a questionnaire question into a step for the stepper
   * if the question has a `measurement` then we use a custom component
   * otherwise we look at the `selectionMechanism` to determine which component to render
   * @param question
   * @returns StepContent
   */
  const quizToStep = useCallback(
    (question: QuestionType): StepContent => {
      let Component = (
        <SingleSelect
          key={question.question_id}
          options={[]}
          initialVal={responses?.[question.question_id]?.[0] as string}
          setCurrentStepHasValue={setCurrentStepHasValue}
          setResponses={setResponses}
          questionId={question.question_id}
        />
      );
      let onContinue = () => {};
      switch (question.answers.measurement) {
        case "dob":
          Component = (
            <DateOfBirthInput
              textFieldOnlyPicker
              value={
                responses?.[question?.question_id]?.[0]
                  ? new Date(responses[question.question_id][0]!)
                  : null
              }
              onChange={(value: Date | null) => {
                setCurrentStepHasValue(!!value);
                setResponses((prev) => {
                  if (value && prev) {
                    prev[question.question_id] = [value];
                  }
                  return prev;
                });
              }}
            />
          );
          onContinue = () => {
            if (responses?.[question.question_id]) {
              updateUserFields({
                birth: formatISO(responses[question.question_id][0] as Date, {
                  representation: "date",
                }),
              });
            }
          };
          break;
        case "weight":
          Component = (
            <OnboardingWeightInput
              userWeight={Number(responses?.[question.question_id])}
              isWeightValid={currentStepValid}
              setIsWeightValid={setCurrentStepValid}
              userUnitPreference={userUnitPreference}
              setUserUnitPreference={setUserUnitPreference}
              setUserWeight={(weight: string) => {
                setCurrentStepHasValue(!!weight);
                setResponses((prev) => {
                  if (prev) {
                    prev[question.question_id] = [+weight];
                  }
                  return prev;
                });
              }}
            />
          );
          onContinue = () => {
            updateUserFields({
              weight: String(responses?.[question.question_id][0]),
              metric_weight: userUnitPreference === ImperialMetricDisplayPreference.metric,
            });
            updateDisplayPreferences({ weight: userUnitPreference });
          };
          break;
        case "height":
          Component = (
            <OnboardingHeightInput
              setUserHeight={(height: string | null) => {
                setCurrentStepHasValue(!!height);
                setResponses((prev) => {
                  if (height && prev) {
                    prev[question.question_id] = [+height];
                  }
                  return prev;
                });
              }}
              userHeight={String(responses?.[question.question_id])}
              userHeightUnit={userUnitPreference}
              setUserHeightUnit={setUserUnitPreference}
              setIsHeightValid={setCurrentStepValid}
            />
          );
          onContinue = () => {
            updateUserFields({
              height: String(responses?.[question.question_id][0]),
              metric_speed_distance: userUnitPreference === ImperialMetricDisplayPreference.metric,
            });
            updateDisplayPreferences({ speed_distance: userUnitPreference });
          };
          break;
        case "sex":
          Component = (
            <SingleSelect
              key={question.question_id}
              initialVal={responses?.[question.question_id]?.[0] as string}
              setCurrentStepHasValue={setCurrentStepHasValue}
              options={(question.answers as AnswerListType).possibility_list}
              setResponses={setResponses}
              skipText={question.answers.skip_answer_text}
              questionId={question.question_id}
              center
            />
          );
          onContinue = () => {
            updateUserFields({ gender: Number(responses?.[question.question_id][0]) });
          };
          break;
        case "race_time": {
          const possibilities = (question.answers as AnswerListType).possibility_list;
          Component = (
            <MultiNumericEntry
              key={question.question_id}
              initialVal={responses?.[question.question_id] as Array<number | null>}
              options={possibilities}
              measurement={question.answers.measurement}
              skipText={question.answers.skip_answer_text}
              setCurrentStepHasValue={setCurrentStepHasValue}
              setCurrentStepValid={setCurrentStepValid}
              setResponses={setResponses}
              questionId={question.question_id}
            />
          );
          break;
        }
        case "distance": {
          const possibilities = (question.answers as AnswerRangeType).possibility_range;
          Component = (
            <NumericDistanceEntry
              key={question.question_id}
              min={possibilities.start}
              max={possibilities.stop}
              skipText={question.answers.skip_answer_text}
              initialVal={responses?.[question.question_id]?.[0] as number | "skip" | undefined}
              userUnitPreference={userUnitPreference}
              setUserUnitPreference={setUserUnitPreference}
              setCurrentStepValid={setCurrentStepValid}
              setCurrentStepHasValue={setCurrentStepHasValue}
              setResponses={setResponses}
              questionId={question.question_id}
            />
          );
          onContinue = () => {
            updateUserFields({
              metric_speed_distance: userUnitPreference === ImperialMetricDisplayPreference.metric,
            });
            updateDisplayPreferences({ speed_distance: userUnitPreference });
          };
          break;
        }
        case "no_measurement":
        default:
          if (question.answers.selection_mechanism === SelectionMechanism.numeric_entry) {
            const possibilities = (question.answers as AnswerRangeType).possibility_range;
            Component = (
              <NumericEntry
                key={question.question_id}
                min={possibilities.start}
                max={possibilities.stop}
                skipText={question.answers.skip_answer_text}
                initialVal={responses?.[question.question_id]?.[0] as number}
                setCurrentStepValid={setCurrentStepValid}
                setCurrentStepHasValue={setCurrentStepHasValue}
                setResponses={setResponses}
                questionId={question.question_id}
              />
            );
          } else if (question.answers.selection_mechanism === SelectionMechanism.exactly_one) {
            const possibilities = (question.answers as AnswerListType).possibility_list;
            Component = (
              <SingleSelect
                key={question.question_id}
                initialVal={responses?.[question.question_id]?.[0] as string}
                setCurrentStepHasValue={setCurrentStepHasValue}
                options={possibilities}
                setResponses={setResponses}
                skipText={question.answers.skip_answer_text}
                questionId={question.question_id}
              />
            );
          } else if (question.answers.selection_mechanism === SelectionMechanism.at_least_one) {
            const possibilities = (question.answers as AnswerListType).possibility_list;
            Component = (
              <MultipleSelect
                key={question.question_id}
                initialVal={responses?.[question.question_id] as Array<string>}
                setCurrentStepHasValue={setCurrentStepHasValue}
                options={possibilities}
                setResponses={setResponses}
                skipText={question.answers.skip_answer_text}
                questionId={question.question_id}
              />
            );
          } else if (
            question.answers.selection_mechanism === SelectionMechanism.exactly_all_ordered
          ) {
            const possibilities = (question.answers as AnswerListType).possibility_list;
            Component = (
              <Ranking
                key={question.question_id}
                initialVal={responses?.[question.question_id] as Array<string>}
                options={possibilities}
                setResponses={setResponses}
                skipText={question.answers.skip_answer_text}
                questionId={question.question_id}
              />
            );
          } else if (
            question.answers.selection_mechanism === SelectionMechanism.multi_numeric_entry
          ) {
            const possibilities = (question.answers as AnswerListType).possibility_list;
            Component = (
              <MultiNumericEntry
                key={question.question_id}
                initialVal={responses?.[question.question_id] as Array<number | null>}
                options={possibilities}
                measurement={question.answers.measurement}
                skipText={question.answers.skip_answer_text}
                setCurrentStepHasValue={setCurrentStepHasValue}
                setCurrentStepValid={setCurrentStepValid}
                setResponses={setResponses}
                questionId={question.question_id}
              />
            );
          }
      }
      return {
        stepId: question.question_id,
        stepName: question.title,
        headline: question.title,
        subheader: question.subtext,
        component: Component,
        onContinue,
        disabled: !responses?.[question.question_id] || !currentStepValid,
      };
    },
    [currentStepValid, responses, updateUserFields, updateDisplayPreferences, userUnitPreference]
  );

  const steps = useMemo(() => {
    if (quizData && JSON.stringify(responses)) {
      return quizData.questions.map(quizToStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizData, quizToStep, JSON.stringify(responses)]);

  const sanitizeResponses = useCallback(
    (responses: Responses) => {
      return quizData?.questions.reduce((acc, cur) => {
        if (!responses[cur.question_id]?.length || responses[cur.question_id][0] === "skip") {
          if (cur.answers.measurement === "distance") {
            acc[String(cur.question_id)] = [0];
          }
          return acc;
        }
        switch (cur.answers.measurement) {
          case "dob":
            acc[String(cur.question_id)] = [
              (responses[cur.question_id][0] as Date).toISOString().replace(/T.*Z/, ""),
            ];
            return acc;
          case "weight":
          case "height":
          case "sex":
            acc[String(cur.question_id)] = [+responses[cur.question_id][0]!];
            return acc;
          case "race_time":
            acc[String(cur.question_id)] = [
              (responses[cur.question_id] as Array<number | null>).reduce(
                (obj, value, index) => {
                  const key = (cur.answers as AnswerListType).possibility_list?.[index]?.key
                    ? `${(cur.answers as AnswerListType).possibility_list[index].key}`
                    : `dur_${index + 1}`;
                  obj[key] = value;
                  return obj;
                },
                {} as Record<string, number | null>
              ),
            ];
            return acc;
          case "no_measurement":
          default:
            if (cur.answers.selection_mechanism === SelectionMechanism.multi_numeric_entry) {
              // Preserve positional nulls so the backend can map each value to the correct distance.
              acc[String(cur.question_id)] = responses[cur.question_id] as Array<number>;
              return acc;
            }
            acc[String(cur.question_id)] = responses[cur.question_id].filter(
              (answer): answer is string | number =>
                typeof answer === "string" || typeof answer === "number"
            );
            return acc;
        }
      }, {} as SanitizedAnswers);
    },
    [quizData?.questions]
  );

  const logQuizEvent = useCallback(
    (clickTarget: ClickTarget) => {
      logEvent(analyticsEventName, {
        click_target: clickTarget,
        source_page_name: analyticsSourcePage,
        step_name: steps?.[activeStep]?.stepName ?? undefined,
        step_ordering: activeStep + 1,
        questionnaire_id: quizData?.questionnaire_id,
      });
    },
    [
      activeStep,
      analyticsEventName,
      analyticsSourcePage,
      logEvent,
      quizData?.questionnaire_id,
      steps,
    ]
  );

  const submitQuiz = useCallback(async () => {
    if (responses) {
      const sanitizedResponses = sanitizeResponses(responses);
      onBeforeSubmit?.(responses);
      const responseObject = buildResponse(
        sanitizedResponses || {},
        quizData?.questionnaire_id || "",
        user.id
      );
      const response = await getQuizResults(responseObject);
      if (response.error) {
        setError(response.error);
      }
      if (response.results) {
        setResults(response.results);
      }
    }
  }, [
    responses,
    sanitizeResponses,
    onBeforeSubmit,
    buildResponse,
    quizData?.questionnaire_id,
    user.id,
    getQuizResults,
    setError,
    setResults,
  ]);

  const handleNext = useCallback(async () => {
    if (steps?.[activeStep].onContinue) {
      logQuizEvent(ClickTarget.continue);
      steps[activeStep].onContinue!();
    }
    if (steps?.length && activeStep === steps.length - 1) {
      logQuizEvent(ClickTarget.see_results);
      submitQuiz();
      setPage(pages.results);
    } else {
      logQuizEvent(ClickTarget.continue);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }, [activeStep, logQuizEvent, pages.results, setPage, steps, submitQuiz]);

  const handleBack = useCallback(() => {
    logQuizEvent(ClickTarget.back);
    // TODO: This doesn't get called if user does a back swipe on iOS
    if (activeStep === 0) {
      if (skipEducation) {
        onFirstStepBack ? onFirstStepBack() : navigateBack();
      } else {
        setPage(pages.education);
      }
    } else {
      setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
    }
  }, [
    activeStep,
    logQuizEvent,
    navigateBack,
    onFirstStepBack,
    pages.education,
    setPage,
    skipEducation,
  ]);

  useEffect(() => {
    setCurrentStepHasValue(!!responses?.[activeStep]);
  }, [responses, activeStep]);

  return {
    activeStep,
    steps,
    handleNext,
    handleBack,
    currentStepHasValue,
    currentStepValid,
    responses,
    quizLoading,
    quizError,
    submitQuiz,
    buttonName,
  };
}

export default useQuiz;
